import { useId } from 'react';
import type { RemixiconComponentType } from '@remixicon/react';
import { Radio, RadioGroup } from '@backstage/ui';

/** One card of `ProfileCards`. */
export interface ProfileCardOption<K extends string> {
  id: K;
  label: string;
  /** What the choice is for, under its name. */
  description?: string;
  /** Its figures, last and smaller (« réservé 0,1 CPU · 704 Mio »). */
  figures?: string;
  icon?: RemixiconComponentType;
}

/**
 * A choice among a few profiles, as radio cards (mockups « F1 » and « U1 »,
 * 2026-10-16): a BUI radio group (arrow keys move the choice), each option a
 * bordered card — its ring, its icon, its name, then what it says. Selected:
 * the accent border and a tint, on top of the filled ring — never the colour
 * alone. Shared by the creation form (`CapacityProfilePicker`,
 * `HaOptionsPicker`) and the « Configuration » tab of a branch page.
 *
 * The group is named by `label` (visible), else `labelledBy`, else
 * `accessibleLabel`. Each card is named by its label alone and described by
 * the rest, so a screen reader says « Standard, radio button, 2 of 4 » before
 * the details.
 */
export function ProfileCards<K extends string>(props: {
  options: readonly ProfileCardOption<K>[];
  value: K;
  onChange: (id: K) => void;
  label?: string;
  labelledBy?: string;
  accessibleLabel?: string;
  description?: string;
  isDisabled?: boolean;
  /** The most cards on one line, room permitting (`data-columns`). */
  columns?: 2 | 3 | 4;
  /** Smaller cards, the name and the figures on two lines. */
  compact?: boolean;
  className?: string;
}) {
  const { options, columns = 4 } = props;
  const idPrefix = useId();
  return (
    <RadioGroup
      label={props.label}
      aria-labelledby={props.label ? undefined : props.labelledBy}
      aria-label={
        props.label || props.labelledBy ? undefined : props.accessibleLabel
      }
      description={props.description}
      value={props.value}
      onChange={next => {
        const option = options.find(entry => entry.id === next);
        if (option) {
          props.onChange(option.id);
        }
      }}
      isDisabled={props.isDisabled}
      className={['waadoo-profile-cards', props.className]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        className="waadoo-profile-cards__grid"
        data-columns={columns}
        data-compact={props.compact ? 'true' : undefined}
      >
        {options.map(option => {
          const Icon = option.icon;
          const nameId = `${idPrefix}-${option.id}-name`;
          const descriptionId = `${idPrefix}-${option.id}-description`;
          const figuresId = `${idPrefix}-${option.id}-figures`;
          const describedBy = [
            option.description ? descriptionId : '',
            option.figures ? figuresId : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <Radio
              key={option.id}
              value={option.id}
              className="waadoo-profile-card"
              aria-labelledby={nameId}
              aria-describedby={describedBy || undefined}
            >
              <span className="waadoo-profile-card__body">
                {Icon && (
                  <span
                    className="waadoo-profile-card__icon"
                    aria-hidden="true"
                  >
                    <Icon aria-hidden focusable="false" />
                  </span>
                )}
                <span className="waadoo-profile-card__text">
                  <span id={nameId} className="waadoo-profile-card__name">
                    {option.label}
                  </span>
                  {option.description && (
                    <span
                      id={descriptionId}
                      className="waadoo-profile-card__description"
                    >
                      {option.description}
                    </span>
                  )}
                  {option.figures && (
                    <span
                      id={figuresId}
                      className="waadoo-profile-card__figures"
                    >
                      {option.figures}
                    </span>
                  )}
                </span>
              </span>
            </Radio>
          );
        })}
      </div>
    </RadioGroup>
  );
}
